/* eslint-disable react/jsx-props-no-spreading */
import React, { useContext, useCallback } from 'react';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import {
  Drawer,
  DrawerHeader,
  DrawerBody,
} from '@one-thd/sui-atomic-components';
import PropTypes from 'prop-types';
import { QuickViewProvider } from '../utils/QuickViewProvider';
import { QuickViewDetails } from './QuickViewDetails';

const { bool, func, shape } = PropTypes;

/**
 * QuickViewDrawer
 *
 * Displays used equipment details in a customizable drawer.
 * Leverages built-in accordion components from product-overview and specifications packages.
 *
 * Props:
 *   isOpen: bool - Drawer visibility
 *   onClose: func - Called when drawer closes
 *   item: shape - Used equipment item data
 *   itemDetail: shape - Detailed product info from API
 *   paginateCarousel: bool - Whether carousel paginates
 */
export const QuickViewDrawer = ({
  isOpen,
  onClose,
  item,
  itemDetail,
  paginateCarousel = true,
}) => {
  const { channel } = useContext(ExperienceContext);
  const isMobile = channel !== 'desktop';

  const handleClose = useCallback(() => {
    onClose?.();
  }, [onClose]);

  if (!isOpen) {
    return null;
  }

  const contextValue = {
    item,
    itemDetail,
    isMobile
  };

  return (
    <div data-testid="used-equipment-quickview-drawer">
      <QuickViewProvider value={contextValue}>
        <Drawer
          open={isOpen}
          onClose={handleClose}
          size={isMobile ? 'full' : 'lg'}
        >
          <DrawerHeader
            title="Used Equipment Details"
            onClose={handleClose}
          />
          <DrawerBody>
            <QuickViewDetails paginateCarousel={paginateCarousel} />
          </DrawerBody>
        </Drawer>
      </QuickViewProvider>
    </div>
  );
};

QuickViewDrawer.displayName = 'QuickViewDrawer';

QuickViewDrawer.propTypes = {
  isOpen: bool.isRequired,
  onClose: func,
  item: shape({}).isRequired,
  itemDetail: shape({}),
  paginateCarousel: bool
};

QuickViewDrawer.defaultProps = {
  onClose: () => {},
  itemDetail: null,
  paginateCarousel: true,
};
